import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import {
  useAdminCreateBroadcast,
  getAdminGetBroadcastsQueryKey,
} from '@/api/auth/admin/admin';
import {
  CreateBroadcastRequestRecipientType,
} from '@/api/auth/missionImpactAuthAPI.schemas';

interface CreateBroadcastDialogProps {
  templateId: string;
  templateEventName: string;
}

/**
 * Dialog for creating a new broadcast with name and recipient type selection
 */
export function CreateBroadcastDialog({ templateId, templateEventName }: CreateBroadcastDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [recipientType, setRecipientType] = useState<CreateBroadcastRequestRecipientType>(
    CreateBroadcastRequestRecipientType.Users
  );

  const { mutate: createBroadcast, isPending } = useAdminCreateBroadcast({
    mutation: {
      onSuccess: (response) => {
        toast.success('Broadcast created');
        queryClient.invalidateQueries({ queryKey: getAdminGetBroadcastsQueryKey({ templateId }) });
        setOpen(false);
        resetForm();
        navigate(`/dashboard/admin/broadcasts/${response.data.id}`);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'CreateBroadcast', templateId });
        toast.error('Failed to create broadcast: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const resetForm = () => {
    setName('');
    setRecipientType(CreateBroadcastRequestRecipientType.Users);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setName(`${templateEventName} Broadcast`);
    } else {
      resetForm();
    }
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error('Please enter a broadcast name');
      return;
    }
    createBroadcast({
      data: {
        name: name.trim(),
        templateId,
        recipientType,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Broadcast
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Broadcast</DialogTitle>
          <DialogDescription>
            Create a new broadcast to send notifications to multiple recipients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Broadcast Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter broadcast name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipientType">Recipient Type</Label>
            <Select
              value={recipientType}
              onValueChange={(value) => setRecipientType(value as CreateBroadcastRequestRecipientType)}
            >
              <SelectTrigger id="recipientType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CreateBroadcastRequestRecipientType.Users}>
                  Users
                </SelectItem>
                <SelectItem value={CreateBroadcastRequestRecipientType.Orgs}>
                  Organizations
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {recipientType === CreateBroadcastRequestRecipientType.Users
                ? 'Broadcast the notification to individual user accounts'
                : 'Broadcast the notification to admins of organizations'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isPending || !name.trim()}>
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
